import React, { FC, useCallback, useState } from 'react';

import { useRouter } from 'expo-router';
import { View } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import { PullToReloadScrollView } from 'components/pull-to-reload/pull-to-reload';
import { QuestSection } from 'components/quests';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useFocusEffect } from 'navigation/hooks/use-focus-effect';
import { CustomEventNames, logLoyaltyEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { useRewardsEvaluation } from 'state/loyalty/hooks/use-loyalty-rewards-evaluation';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { LoyaltyReward } from 'state/loyalty/types';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';
import { waitForDuration } from 'utils/wait-for-duration';

import { LoyaltyNotEnrolled } from '../loyalty-not-enrolled';

import { LoyaltyRewardsExpiringPoints } from './loyalty-rewards-expiring-points';
import { LoyaltyRewardsList } from './loyalty-rewards-list';
import { LoyaltyRewardsSkeletonLoader } from './loyalty-rewards-skeleton-loader';
import { LoyaltyRewardsListContainer } from './loyalty-rewards.styled';
import { useFilteredRewardCategories } from './utils';

/**
 *
 * LoyaltyRewards displays a list of available rewards that can be redeemed at checkout
 *
 */
const LoyaltyRewards: FC = () => {
  const { user, loading: userDataIsLoading, isAuthenticated } = useAuthContext();
  const {
    engineRewardsMap,
    loading: rewardDataIsLoading,
    sanityRewardsMap,
    refetchRewards,
  } = useLoyaltyRewardsList();

  useFocusEffect(
    useCallback(() => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'loyaltyRewards',
      });
    }, [])
  );

  const { rewardsEvaluationMap } = useRewardsEvaluation();

  const [isForceRefreshing, setForceRefresh] = useState<boolean>(false);

  const router = useRouter();

  const loyaltyId = user?.loyaltyId;

  const rewardCategories = useFilteredRewardCategories();

  // Refetch for updated crowns when navigating to page
  useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const handleRewardOnPress = useCallback(
    (reward: LoyaltyReward) => {
      const rewardId = reward._id;
      logLoyaltyEvent({
        name: CustomEventNames.REWARD_SELECTED,
        incentives: [reward],
      });

      router.navigate(`${routes.loyaltyRewardsList}/${rewardId}`);
    },
    [router]
  );

  const forceRefresh = async () => {
    // We need this delay in order to avoid cutting the spinning animation
    await waitForDuration(1500);
    if (loyaltyId) {
      setForceRefresh(true);
      await refetchRewards(loyaltyId);
      setForceRefresh(false);
    }
  };

  if (userDataIsLoading || rewardDataIsLoading || isForceRefreshing) {
    return <LoyaltyRewardsSkeletonLoader />;
  }

  if (isAuthenticated && !loyaltyId) {
    return <LoyaltyNotEnrolled />;
  }

  if (!rewardCategories?.length || !sanityRewardsMap) {
    return <LoyaltyRewardsSkeletonLoader />;
  }

  return (
    <PullToReloadScrollView
      progressViewOffset={10}
      onRefresh={forceRefresh}
      contentContainerStyle={centeredRowContainerStyles.container}
      isDisabled={!isAuthenticated}
    >
      <View style={centeredRowContainerStyles.content}>
        <LoyaltyRewardsExpiringPoints />
        <QuestSection showHeader={false} />
        <LoyaltyRewardsListContainer>
          <LoyaltyRewardsList
            handleOnPress={handleRewardOnPress}
            rewardCategories={rewardCategories}
            engineRewardsMap={engineRewardsMap}
            rewardsEvaluationMap={rewardsEvaluationMap}
            sanityRewardsMap={sanityRewardsMap}
          />
        </LoyaltyRewardsListContainer>
      </View>
    </PullToReloadScrollView>
  );
};

export default LoyaltyRewards;
